import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const sb = supabase as any;

interface UseQuizSessionOpts {
  /** Resume an existing session by id (from ?resume=) */
  resumeId?: string | null;
  /** mcq ids currently loaded in the practice view */
  mcqIds: string[];
  /** session mode (tutor/test) */
  mode?: "tutor" | "test";
  /** topic id, optional */
  topicId?: string | null;
}

interface ResumedSession {
  id: string;
  mcq_ids: string[];
  current_index: number;
  session_mode: "tutor" | "test";
  answers: any[];
}

/**
 * Lightweight wrapper around the quiz_sessions table.
 * - If `resumeId` is provided, loads that session once.
 * - Otherwise lazily creates/updates a session for the current mcq pool.
 * - Exposes `persistIndex` to keep current_index in sync as the user moves.
 */
export const useQuizSession = ({ resumeId, mcqIds, mode = "tutor", topicId }: UseQuizSessionOpts) => {
  const { user } = useAuth();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [resumed, setResumed] = useState<ResumedSession | null>(null);
  const initialized = useRef(false);

  // Load a resume session
  useEffect(() => {
    if (!user || !resumeId || initialized.current) return;
    initialized.current = true;
    (async () => {
      const { data, error } = await sb
        .from("quiz_sessions")
        .select("id, mcq_ids, current_index, session_mode, answers")
        .eq("id", resumeId)
        .eq("user_id", user.id)
        .maybeSingle();
      if (!error && data) {
        setSessionId(data.id);
        setResumed(data as ResumedSession);
      }
    })();
  }, [user, resumeId]);

  // Lazily create a session when a fresh mcq pool exists (no resume)
  useEffect(() => {
    if (!user || resumeId || sessionId || mcqIds.length === 0) return;
    (async () => {
      const { data, error } = await sb
        .from("quiz_sessions")
        .insert({
          user_id: user.id,
          mcq_ids: mcqIds,
          session_mode: mode,
          status: "in_progress",
          current_index: 0,
          answers: [],
          topic_id: topicId ?? null,
        })
        .select("id")
        .maybeSingle();
      if (!error && data) setSessionId(data.id);
    })();
  }, [user, resumeId, sessionId, mcqIds, mode, topicId]);

  const persistIndex = async (idx: number) => {
    if (!sessionId || !user) return;
    await sb
      .from("quiz_sessions")
      .update({ current_index: idx })
      .eq("id", sessionId)
      .eq("user_id", user.id);
  };

  const markCompleted = async () => {
    if (!sessionId || !user) return;
    await sb
      .from("quiz_sessions")
      .update({ status: "completed", completed_at: new Date().toISOString() })
      .eq("id", sessionId)
      .eq("user_id", user.id);
  };

  return { sessionId, resumed, persistIndex, markCompleted };
};
